import {
	ANIMAL_RECORD_CONDITION_MAX,
	type AnimalFilters,
	type AnimalRecordCondition,
	type AnimalSex,
	type LivestockStatus,
} from "@packages/shared-types";
import { LivestockStatus as LivestockStatusEnum } from "@packages/shared-types/livestock";

/**
 * The status shown when the user hasn't chosen one — the list defaults to living
 * head. Absence of the `status` URL param means "All statuses"; this default is
 * seeded into the URL on first load so "All" stays representable as an explicit
 * user choice.
 */
export const DEFAULT_ANIMAL_STATUS: LivestockStatus =
	LivestockStatusEnum.ACTIVE;

/**
 * URL ⇄ AnimalFilters serialization — the single source of filter truth.
 *
 * The animals list keeps its whole cohort filter in the URL (so filters survive
 * Back and are shareable). This module is the pure, framework-free seam between
 * the route's flat search params and the shared {@link AnimalFilters} shape;
 * `validateSearch` and the list component both go through it, and the round-trip
 * (`filtersToSearch(searchToFilters(x)) ≈ x`) is unit-tested. The URL keys are
 * intentionally shorter than the contract field names (`herds` ⇄ `herdIds`,
 * `birthFrom` ⇄ `birthDateFrom`, …) to keep shared links compact.
 *
 * `search` is deliberately NOT part of this mapping — it stays a debounced local
 * input at the callsite (it is not in the plan's URL-key list).
 */

/** The flat, URL-facing search-param shape for the livestock index route. */
export type AnimalFilterSearch = {
	sex?: AnimalSex;
	breeds?: string;
	herds?: string;
	status?: LivestockStatus;
	birthFrom?: string;
	birthTo?: string;
	acqFrom?: string;
	acqTo?: string;
	/**
	 * Record conditions as a structured array. TanStack Router's search
	 * serializer JSON-encodes it exactly once, so shared links stay compact and
	 * are not double-quoted (a pre-stringified string would be re-encoded).
	 */
	records?: AnimalRecordCondition[];
};

const SEX_VALUES = new Set(["MALE", "FEMALE"]);
const STATUS_VALUES = new Set(["ACTIVE", "SOLD", "DECEASED", "TRANSFERRED"]);

/** Split a CSV param into a trimmed, non-empty, de-duplicated string list. */
function parseCsv(raw: unknown): string[] | undefined {
	if (typeof raw !== "string" || raw.length === 0) return undefined;
	const items = Array.from(
		new Set(
			raw
				.split(",")
				.map((v) => v.trim())
				.filter((v) => v.length > 0),
		),
	);
	return items.length > 0 ? items : undefined;
}

function parseString(raw: unknown): string | undefined {
	return typeof raw === "string" && raw.length > 0 ? raw : undefined;
}

/**
 * Best-effort parse of the `records` conditions. Accepts either the structured
 * array (the shape TanStack Router now round-trips) or a JSON string — so
 * hand-typed URLs and links shared from the earlier pre-stringified format still
 * restore. Malformed input is dropped rather than thrown — a bad shared link
 * must still render the list. Only structurally-sound conditions survive; the
 * backend re-validates authoritatively.
 */
function parseRecords(raw: unknown): AnimalRecordCondition[] | undefined {
	let decoded: unknown = raw;
	if (typeof raw === "string") {
		if (raw.length === 0) return undefined;
		try {
			decoded = JSON.parse(raw);
		} catch {
			return undefined;
		}
	}
	if (!Array.isArray(decoded)) return undefined;
	const conditions: AnimalRecordCondition[] = [];
	for (const entry of decoded) {
		if (typeof entry !== "object" || entry === null) continue;
		const c = entry as Record<string, unknown>;
		if (c.kind !== "operation" && c.kind !== "measurement") continue;
		if (c.presence !== "has" && c.presence !== "hasNot") continue;
		if (typeof c.type !== "string" || c.type.length === 0) continue;
		const condition: AnimalRecordCondition = {
			kind: c.kind,
			type: c.type as AnimalRecordCondition["type"],
			presence: c.presence,
		};
		if (typeof c.from === "string" && c.from.length > 0)
			condition.from = c.from;
		if (typeof c.to === "string" && c.to.length > 0) condition.to = c.to;
		conditions.push(condition);
		if (conditions.length >= ANIMAL_RECORD_CONDITION_MAX) break;
	}
	return conditions.length > 0 ? conditions : undefined;
}

/**
 * Parse the route's raw search params into a typed {@link AnimalFilters}.
 * Unknown/invalid values are dropped (defensive against hand-edited URLs).
 */
export function searchToFilters(
	search: Record<string, unknown>,
): AnimalFilters {
	const filters: AnimalFilters = {};

	const sex = parseString(search.sex);
	if (sex && SEX_VALUES.has(sex)) filters.sex = sex as AnimalSex;

	const status = parseString(search.status);
	if (status && STATUS_VALUES.has(status))
		filters.status = status as LivestockStatus;

	const breeds = parseCsv(search.breeds);
	if (breeds) filters.breeds = breeds;

	const herdIds = parseCsv(search.herds);
	if (herdIds) filters.herdIds = herdIds;

	const birthFrom = parseString(search.birthFrom);
	if (birthFrom) filters.birthDateFrom = birthFrom;
	const birthTo = parseString(search.birthTo);
	if (birthTo) filters.birthDateTo = birthTo;

	const acqFrom = parseString(search.acqFrom);
	if (acqFrom) filters.acquisitionDateFrom = acqFrom;
	const acqTo = parseString(search.acqTo);
	if (acqTo) filters.acquisitionDateTo = acqTo;

	const records = parseRecords(search.records);
	if (records) filters.records = records;

	return filters;
}

/**
 * Serialize {@link AnimalFilters} back to the flat, URL-facing shape. Empty
 * dimensions are emitted as `undefined` so TanStack Router strips them from the
 * URL — a cleared filter leaves no orphan param behind.
 */
export function filtersToSearch(filters: AnimalFilters): AnimalFilterSearch {
	return {
		sex: filters.sex,
		status: filters.status,
		breeds: filters.breeds?.length ? filters.breeds.join(",") : undefined,
		herds: filters.herdIds?.length ? filters.herdIds.join(",") : undefined,
		birthFrom: filters.birthDateFrom || undefined,
		birthTo: filters.birthDateTo || undefined,
		acqFrom: filters.acquisitionDateFrom || undefined,
		acqTo: filters.acquisitionDateTo || undefined,
		records: filters.records?.length ? filters.records : undefined,
	};
}

/**
 * The count of active *advanced* dimensions — everything the sheet owns. Drives
 * the "Filtros" button badge. `status` and `search` are excluded because they
 * live inline (chips + search box), not behind the sheet.
 */
export function countAdvancedFilters(filters: AnimalFilters): number {
	let count = 0;
	if (filters.sex) count++;
	if (filters.breeds?.length) count++;
	if (filters.herdIds?.length) count++;
	if (filters.birthDateFrom || filters.birthDateTo) count++;
	if (filters.acquisitionDateFrom || filters.acquisitionDateTo) count++;
	if (filters.records?.length) count++;
	return count;
}
